<script setup lang="ts">
import type { Page } from '~/types';

// ─── Preview mode ─────────────────────────────────────────────────────
const { isPreview, previewToken, previewVersion } = useContentPreview();

const { path } = useRoute();
const url = useRequestURL();
const { fileUrl } = useFiles();
const { data: globals } = await useGlobals();

function safeFileId(fileOrId: unknown): string | undefined {
  if (!fileOrId) return undefined;
  if (typeof fileOrId === 'string') return fileOrId;

  if (typeof fileOrId === 'object' && 'id' in fileOrId) {
    return (fileOrId as { id?: string }).id;
  }

  return undefined;
}

const pageFilter = computed(() => {
  let finalPath;

  if (path === '/') {
    // Match the homepage
    finalPath = '/';
  } else if (path.endsWith('/')) {
    // Remove any other trailing slash
    finalPath = path.slice(0, -1);
  } else {
    // Match any other page
    finalPath = path;
  }

  return { permalink: { _eq: finalPath } };
});

// ─── FIRST CALL: Fetch minimal page data to get the blocks that apply ──────────
// This call returns the page along with a minimal "blocks" field so we know which block collections are used.
const { data: pageBlocks, error: pageBlocksError } = await useAsyncData(
  path + '-blocks',
  () => {
    return useDirectusProxy<Page[]>({
      operation: 'readItems',
      collection: 'pages',
      params: {
        filter: unref(pageFilter),
        fields: [
          '*',
          {
            seo: ['*', { og_image: ['id', 'title', 'description'] }],
            blocks: ['id', 'collection'], // minimal fields: just the id and block type
          },
        ],
        limit: 1,
      },
      ...(isPreview ? { previewToken: previewToken || undefined } : {}),
      ...(isPreview && previewVersion ? { previewVersion: previewVersion || undefined } : {}),
    });
  },
  {
    watch: [],
    transform: (data) => {
      return data[0];
    },
  },
);

if (pageBlocksError.value) {
  throw createError({
    statusCode: 502,
    statusMessage: `Page lookup failed: ${pageBlocksError.value.message}`,
  });
}

// ─── Define a mapping of block collection names to the fields you need ──────────
const blockFieldMapping = {
  block_hero: [
    'id',
    'title',
    'headline',
    'content',
    'hero_type',
    'hero_size',
    'text_position',
    { image: ['id', 'title', 'description'] },
    { foreground_image: ['id', 'title', 'description'] },
    'image_position',
    {
      button_group: ['*', { buttons: ['*', { page: ['permalink'], post: ['slug'] }] }],
    },
    {
      carousel_items: [{ directus_files_id: ['id', 'title', 'description'] }],
    },
  ],
  block_faqs: ['id', 'title', 'faqs', 'headline', 'alignment'],
  block_richtext: ['id', 'title', 'headline', 'content', 'alignment'],
  block_counter: [
    '*',
    { counter_rows: ['*'] },
    {
      button_group: ['*', { buttons: ['*', { page: ['permalink'], post: ['slug'] }] }],
    },
  ],
  block_testimonials: [
    'id',
    'title',
    'headline',
    'layout_type',
    {
      testimonials: [
        {
          testimonials_id: [
            'id',
            'title',
            'subtitle',
            'content',
            'company',
            'company_logo',
            'date_created',
            { image: ['id', 'title', 'description'] },
          ],
        },
      ],
    },
  ],
  block_timeline: ['id', 'title', 'headline', { timeline_items: ['id', 'title', 'date', 'description'] }],
  block_quote: ['id', 'title', 'subtitle', 'content'],
  block_cta: [
    'id',
    'title',
    'headline',
    'content',
    {
      button_group: ['*', { buttons: ['*', { page: ['permalink'], post: ['slug'] }] }],
    },
  ],
  block_form: ['id', 'title', 'headline', { form: ['*'] }],
  block_cardgrid: [
    'id',
    'title',
    'ui',
    {
      cards: [
        'id',
        'title',
        'description',
        'icon',
        'custom_icon',
        'custom_width',
        'custom_height',
        'orientation',
        { image: ['id', 'title', 'description'] },
        {
          button_group: ['*', { buttons: ['*', { page: ['permalink'], post: ['slug'] }] }],
        },
      ],
    },
  ],
  block_card: [
    'id',
    'title',
    'description',
    'icon',
    'custom_icon',
    'custom_width',
    'custom_height',
    'orientation',
    { image: ['id', 'title', 'description'] },
    {
      button_group: ['*', { buttons: ['*', { page: ['permalink'], post: ['slug'] }] }],
    },
  ],
  block_logocloud: [
    'id',
    'title',
    'headline',
    {
      logos: ['id', 'url', 'custom_class', { directus_files_id: ['id', 'title', 'description'] }],
    },
  ],
  block_gallery: [
    'id',
    'title',
    'headline',
    'group_by_category',
    {
      gallery_items: [
        {
          directus_files_id: [
            'id',
            'title',
            'description',
            {
              categories: [{ categories_id: ['title', 'headline', 'color'] }],
            },
          ],
        },
      ],
    },
    'add_button',
    {
      button_group: ['*', { buttons: ['*', { page: ['permalink'], post: ['slug'] }] }],
    },
  ],
  block_steps: [
    'id',
    'title',
    'headline',
    'show_step_numbers',
    'alternate_image_position',
    {
      steps: [
        'id',
        'title',
        'content',
        { image: ['id', 'title', 'description'] },
        {
          button_group: ['*', { buttons: ['*', { page: ['permalink'], post: ['slug'] }] }],
        },
      ],
    },
  ],
  block_columns: [
    'id',
    'title',
    'headline',
    {
      rows: [
        'title',
        'headline',
        'content',
        'image_position',
        { image: ['id', 'title', 'description'] },
        {
          button_group: ['*', { buttons: ['*', { page: ['permalink'], post: ['slug'] }] }],
        },
      ],
    },
  ],
  block_divider: ['id', 'title', 'emphasize', { image: ['id', 'title', 'description'] }],
  block_team: ['*'],
  block_category_gallery: ['*'],
  block_pulsing_grid: ['*'],
  block_html: ['*'],
  block_video: ['*'],
  block_virtual_tour: ['id', 'title', 'tour_url', 'status'],
  block_cardgroup: ['*'],
};

// ─── Build a stable snapshot of block field requirements — plain values, not reactive computed ──────────
const _rawBlocks = pageBlocks.value?.blocks ?? [];
const _uniqueBlockTypes = [...new Set(
  _rawBlocks.flatMap((block) => typeof block === 'object' && block?.collection ? [block.collection] : [])
)];
const stableBlockFieldMap: Record<string, unknown> = {};
_uniqueBlockTypes.forEach((type) => {
  if (type && (blockFieldMapping as Record<string, unknown>)[type]) {
    stableBlockFieldMap[type] = (blockFieldMapping as Record<string, unknown>)[type];
  }
});

// ─── SECOND CALL: Fetch full page data using only the applicable block fields ──────────
const { data: page, error: pageError } = await useAsyncData(
  path + '-full',
  () => {
    return useDirectusProxy<Page[]>({
      operation: 'readItems',
      collection: 'pages',
      params: {
        filter: unref(pageFilter),
        fields: [
          '*',
          {
            seo: ['*', { og_image: ['id', 'title', 'description'] }],
            blocks: [
              'id',
              'collection',
              'hide_block',
              // Use our dynamic structure for the item field so that only the fields
              // for blocks that are actually used are requested.
              { item: stableBlockFieldMap },
            ],
          },
        ],
        limit: 1,
      },
      ...(isPreview ? { previewToken: previewToken || undefined } : {}),
      ...(isPreview && previewVersion ? { previewVersion: previewVersion || undefined } : {}),
    });
  },
  {
    deep: true,
    watch: [],
    transform: (data) => data[0],
  },
);

if (pageError.value) {
  throw createError({
    statusCode: 502,
    statusMessage: `Page content lookup failed: ${pageError.value.message}`,
  });
}

if (useRuntimeConfig().public.directus.rest.logLevel === 'trace') {
  console.info('Page Data(id): ', page?.value?.id);
  console.info('Page Data(title): ', page?.value?.title);
  console.info('Page Data(permalink): ', page?.value?.permalink);
}

// Error Handling
if (!unref(page)) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found' });
}

// ─── Compute metadata (SEO, OG, etc.) as before ──────────
// const metadata = computed(() => {
//   const pageData = unref(page);
//   let ogImage;
//   if (pageData?.seo?.og_image) {
//     ogImage = fileUrl(pageData.seo.og_image) + '?width=1200';
//   } else if (pageData?.blocks?.[0]?.item?.image) {
//     ogImage = fileUrl(pageData.blocks[0].item.image) + '?width=1200';
//   } else if (globals.og_image) {
//     ogImage = fileUrl(globals.og_image) + '?width=1200';
//   } else {
//     ogImage = undefined;
//   }
//   return {
//     title: pageData?.seo?.title ?? pageData?.title ?? undefined,
//     description: pageData?.seo?.meta_description ?? pageData?.summary ?? undefined,
//     image: ogImage,
//     url: pageData?.seo?.canonical_url ?? url?.href,
//   };
// });
const metadata = computed(() => {
  const pageData = unref(page);
  const seoObj = typeof pageData?.seo === 'object' ? (pageData?.seo as import('~/types').SEO) : null;
  const imageId = safeFileId(seoObj?.og_image) ?? safeFileId(globals.value?.og_image);

  if (process.env.LOG_LEVEL === 'debug') {
    console.debug('[page] SEO metadata:', {
      pageId: pageData?.id,
      permalink: pageData?.permalink,
      seoId: seoObj?.id,
      ogImageId: imageId,
    });
  }

  return {
    title: seoObj?.title ?? pageData?.title ?? undefined,
    description: seoObj?.meta_description ?? undefined,
    image: imageId ? fileUrl(imageId) : undefined,
    url: seoObj?.canonical_url ?? url?.href,
    logo: 'https://consus-cms.ridiculousengineering.com/assets/d111bc32-e2be-42fa-9024-4d6496c30f72?width=246&height=68',
  };
});

if (process.env.LOG_LEVEL === 'debug') {
  console.log('metadata', metadata.value);
}

defineOgImage('Page', {
  title: unref(metadata)?.title,
  description: unref(metadata)?.description,
  imageUrl: unref(metadata)?.image,
  logo: unref(metadata)?.logo,
});

// JSON-LD
useSchemaOrg([
  defineWebPage({
    name: unref(metadata)?.title,
    description: unref(metadata)?.description,
  }),
]);

useHead({
  title: () => unref(metadata)?.title,
  link: [
    { rel: 'canonical', href: () => unref(metadata)?.url },
    { rel: 'icon', type: 'image/ico', href: '/favicon.ico' },
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
    { rel: 'icon', type: 'image/png', sizes: '192x192', href: '/android-chrome-192x192.png' },
    { rel: 'icon', type: 'image/png', sizes: '512x512', href: '/android-chrome-512x512.png' },
  ],
});

useSeoMeta({
  description: unref(metadata)?.description,
  ogTitle: unref(metadata)?.title,
  ogDescription: unref(metadata)?.description,
  ogImage: unref(metadata)?.image,
  ogUrl: unref(metadata)?.url,
  twitterTitle: unref(metadata)?.title,
  twitterDescription: unref(metadata)?.description,
  twitterImage: unref(metadata)?.image,
  twitterCard: 'summary_large_image',
});

useServerSeoMeta({
  title: () => unref(metadata)?.title,
  description: () => unref(metadata)?.description,
  ogTitle: () => unref(metadata)?.title,
  ogDescription: () => unref(metadata)?.description,
});
</script>
<template>
  <NuxtErrorBoundary>
    <!-- Render the page using the PageBuilder component -->
    <PageBuilder v-if="page" :page="page as Page" />

    <!-- If there is an error, display it using the VAlert component -->
    <template #error="{ error }">
      <BlockContainer>
        <VAlert type="error">{{ error }}</VAlert>
      </BlockContainer>
    </template>
  </NuxtErrorBoundary>
</template>
